import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Empty,
  Input,
  Segmented,
  Select,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import { observer } from 'mobx-react-lite';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserIdMenu } from '../../components/common/UserIdMenu';
import type {
  ChildSession,
  InspectionSession,
  RailSection,
} from '../../services/roomInspectorApi';
import { RoomInspectorStore } from '../../stores/roomInspectorStore';
import { ArtifactPreview } from './ArtifactPreview';
import {
  emptyInspectorQuery,
  type InspectorQuery,
  inspectorView,
  parseInspectorQuery,
  queryFromURL,
  queryToURL,
} from './inspectorQuery';
import { SessionTranscript } from './SessionTranscript';
import './roomInspector.css';

function timestamp(value: string | number): string {
  const number = Number(value);
  return number > 0 ? new Date(number).toLocaleString() : '—';
}

const SessionTree = observer(
  ({
    store,
    select,
  }: {
    store: RoomInspectorStore;
    select: (id: string) => void;
  }) => {
    const [children, setChildren] = useState<Record<string, ChildSession[]>>(
      {},
    );
    useEffect(() => {
      const snapshot = store.snapshot;
      if (snapshot)
        setChildren((previous) => ({
          ...previous,
          [snapshot.session.agentSessionId]: snapshot.childSessions,
        }));
    }, [store.snapshot]);
    const nested = (
      items: ChildSession[],
      parent: string,
      seen = new Set<string>(),
    ): ReactNode =>
      items
        .filter(
          (item) =>
            item.parentAgentSessionId === parent &&
            !seen.has(item.agentSessionId),
        )
        .map((item) => (
          <div className="inspector-child" key={item.agentSessionId}>
            <Button
              type="text"
              className={
                store.query.sessionId === item.agentSessionId
                  ? 'is-selected'
                  : ''
              }
              onClick={() => select(item.agentSessionId)}
            >
              ↳ {item.title || item.agentSessionId}
            </Button>
            {nested(
              items,
              item.agentSessionId,
              new Set([...seen, item.agentSessionId]),
            )}
          </div>
        ));
    const row = (session: InspectionSession) => (
      <div key={session.agentSessionId}>
        <button
          type="button"
          className={`inspector-session-row ${store.query.sessionId === session.agentSessionId ? 'is-selected' : ''}`}
          onClick={() => select(session.agentSessionId)}
        >
          <span>
            {session.accessMode === 'private' ? '🔒 ' : ''}
            {session.title || '未命名会话'}
          </span>
          <small>
            {store.overview?.members.find(
              (member) => member.user.userId === session.createdByUserId,
            )?.user.displayName || session.createdByUserId}{' '}
            · {session.status || 'idle'}
          </small>
        </button>
        {children[session.agentSessionId] &&
          nested(children[session.agentSessionId], session.agentSessionId)}
      </div>
    );
    const section = (value: RailSection, label: string, pinned = false) => (
      <div
        className="inspector-tree-section"
        key={pinned ? 'pinned' : value.railSectionKey}
      >
        <h3>{label}</h3>
        {value.sessions.map(row)}
        {!value.sessions.length && <small>暂无会话</small>}
        {value.nextCursor && (
          <Button
            size="small"
            block
            loading={store.loadingSections.has(
              pinned ? 'pinned' : value.railSectionKey,
            )}
            onClick={() => store.loadSection(value.railSectionKey, pinned)}
          >
            显示更多
          </Button>
        )}
      </div>
    );
    if (!store.rail) return null;
    return (
      <>
        {store.rail.pinned.sessions.length > 0 &&
          section(store.rail.pinned, '置顶', true)}
        {store.rail.sections.map((value) =>
          section(
            value,
            value.railSectionKey === 'conversations'
              ? '未关联项目'
              : `📁 ${store.rail?.projects.find((project) => project.sectionKey === value.railSectionKey)?.label || store.rail?.projects.find((project) => project.sectionKey === value.railSectionKey)?.path || value.railSectionKey}`,
          ),
        )}
        {store.rail.nextProjectsCursor && (
          <Button
            block
            loading={store.projectsLoading}
            onClick={store.moreProjects}
          >
            更多项目
          </Button>
        )}
      </>
    );
  },
);

export const RoomInspectorPage = observer(() => {
  const [params, setParams] = useSearchParams();
  const [store] = useState(() => new RoomInspectorStore());
  const [userInput, setUserInput] = useState(params.get('userId') || '');
  const [roomInput, setRoomInput] = useState(params.get('roomId') || '');
  const [validation, setValidation] = useState('');
  const [mobilePanel, setMobilePanel] = useState<
    'sessions' | 'messages' | 'artifacts'
  >('messages');
  useEffect(() => {
    setMobilePanel(
      store.query.roomId && !store.query.sessionId ? 'sessions' : 'messages',
    );
  }, [store.query.roomId, store.query.sessionId]);
  const search = params.toString();
  useEffect(() => {
    const currentParams = new URLSearchParams(search);
    setUserInput(currentParams.get('userId') || '');
    setRoomInput(currentParams.get('roomId') || '');
    try {
      const query = queryFromURL(currentParams);
      setValidation('');
      void store.loadQuery(query);
    } catch (error) {
      store.dispose();
      setValidation((error as Error).message);
    }
  }, [search, store]);
  useEffect(() => () => store.dispose(), [store]);
  const navigate = (query: InspectorQuery) =>
    setParams(queryToURL(query, params));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    try {
      const query = parseInspectorQuery({
        userId: userInput,
        roomId: roomInput,
      });
      setValidation('');
      navigate(query);
    } catch (error) {
      setValidation((error as Error).message);
    }
  };
  const selectRoom = (id: string) =>
    navigate({ ...store.query, roomId: id, sessionId: '' });
  const selectUser = (id: string) =>
    navigate({ ...store.query, userId: id, sessionId: '' });
  const selectSession = (id: string) => {
    setMobilePanel('messages');
    navigate({ ...store.query, sessionId: id });
  };
  const overview = store.overview;
  const view = inspectorView(store.query);
  const users = new Map(
    overview?.members.map((member) => [member.user.userId, member.user]) || [],
  );
  return (
    <main className="room-inspector">
      <form className="inspector-filter" onSubmit={submit}>
        <div className="inspector-search-heading">
          <h1>房间巡检</h1>
          <Tag color="blue">只读</Tag>
        </div>
        <label htmlFor="inspector-user-id">
          用户 ID
          <Input
            id="inspector-user-id"
            aria-label="用户 ID"
            placeholder="数字 UID（可选）"
            allowClear
            value={userInput}
            onChange={(event) => setUserInput(event.target.value)}
          />
        </label>
        <label htmlFor="inspector-room-id">
          房间 ID
          <Input
            id="inspector-room-id"
            aria-label="房间 ID"
            placeholder="房间 ID 或房间链接（可选）"
            allowClear
            value={roomInput}
            onChange={(event) => setRoomInput(event.target.value)}
          />
        </label>
        <Button htmlType="submit" type="primary" loading={store.loading}>
          查询
        </Button>
        <Button
          onClick={() => {
            setUserInput('');
            setRoomInput('');
            navigate({ ...emptyInspectorQuery });
          }}
        >
          清空
        </Button>
      </form>
      {validation && <Alert role="alert" type="error" title={validation} />}
      {store.error && (
        <Alert
          role="alert"
          type="error"
          title={store.error}
          action={
            <Button size="small" onClick={() => store.loadQuery(store.query)}>
              重试
            </Button>
          }
        />
      )}
      {store.loading && (
        <div className="inspector-placeholder">
          <Spin tip="正在查询" />
        </div>
      )}
      {!store.loading && !validation && view === 'empty' && (
        <div className="inspector-placeholder">
          <Empty description="输入用户 ID 或房间 ID 开始巡检" />
        </div>
      )}
      {!store.loading && view === 'user' && store.userOverview && (
        <section className="inspector-user-rooms">
          <h2>
            <Avatar src={store.userOverview.user.avatarUrl}>
              {store.userOverview.user.displayName?.slice(0, 1)}
            </Avatar>{' '}
            {store.userOverview.user.displayName || store.query.userId}
          </h2>
          <p>
            UID {store.query.userId} · {store.userOverview.roomCount}
            {store.userOverview.roomCountCapped ? '+' : ''} 个房间 ·{' '}
            {store.userOverview.sessionCount} 个会话（私密{' '}
            {store.userOverview.privateSessionCount}）
          </p>
          <div className="inspector-room-filters">
            <Segmented
              disabled={store.roomsLoading}
              options={[
                { label: '全部房间', value: 'all' },
                { label: '我创建的', value: 'owner' },
                { label: '我加入的', value: 'member' },
              ]}
              value={store.relation}
              onChange={(value) =>
                store.filterRooms(String(value), store.includeLeft)
              }
            />
            <Checkbox
              disabled={store.roomsLoading}
              checked={store.includeLeft}
              onChange={(event) =>
                store.filterRooms(store.relation, event.target.checked)
              }
            >
              包含已退出的房间
            </Checkbox>
          </div>
          <div className="inspector-table">
            <table>
              <thead>
                <tr>
                  <th>房间</th>
                  <th>角色</th>
                  <th>加入时间</th>
                  <th>他的会话</th>
                  <th>最近活动</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {store.rooms.map((item) => (
                  <tr key={item.room.roomId}>
                    <td>
                      {item.room.name}
                      {item.left && <Tag>已退出</Tag>}
                    </td>
                    <td>{item.role === 'owner' ? '房主' : '成员'}</td>
                    <td>{timestamp(item.joinedAtUnixMs)}</td>
                    <td>
                      {item.sessionCount}（🔒 {item.privateSessionCount}）
                    </td>
                    <td>{timestamp(item.room.updatedAtUnixMs)}</td>
                    <td>
                      <Button
                        type="link"
                        onClick={() => selectRoom(item.room.roomId)}
                      >
                        查看 →
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!store.rooms.length && <Empty description="没有符合条件的房间" />}
          {store.roomsCursor && (
            <Button
              loading={store.roomsLoading}
              onClick={() => store.loadRooms(true)}
            >
              加载更多房间
            </Button>
          )}
        </section>
      )}
      {!store.loading && overview && view === 'room' && (
        <>
          <div className="inspector-room-header">
            <div className="inspector-room-summary">
              <h2 title={overview.room.name}>
                {overview.room.name || '未命名房间'}
              </h2>
              <span>
                {overview.sessionCount} 个会话 · {overview.artifactCount} 个产物
              </span>
              <Tooltip
                title={
                  <div>
                    <div>
                      房主：
                      {overview.room.owner.displayName ||
                        overview.room.owner.userId}
                    </div>
                    <div>创建于 {timestamp(overview.room.createdAtUnixMs)}</div>
                    <div>{overview.room.roomId}</div>
                  </div>
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="房间详情"
                  icon={<InfoCircleOutlined />}
                />
              </Tooltip>
            </div>
            {store.query.userId && store.rooms.length > 0 && (
              <div>
                <Select
                  aria-label="切换用户的房间"
                  style={{ minWidth: 220 }}
                  value={store.query.roomId}
                  options={[
                    { label: overview.room.name, value: store.query.roomId },
                    ...store.rooms
                      .filter((item) => item.room.roomId !== store.query.roomId)
                      .map((item) => ({
                        label: item.room.name,
                        value: item.room.roomId,
                      })),
                  ]}
                  onChange={selectRoom}
                />
                {store.roomsCursor && (
                  <Button
                    size="small"
                    loading={store.roomsLoading}
                    onClick={() => store.loadRooms(true)}
                  >
                    更多房间
                  </Button>
                )}
              </div>
            )}
          </div>
          {store.selectedUserAbsent && (
            <Alert
              type="info"
              title="该用户不在此房间"
              description="未找到该用户的成员或历史会话记录。"
              action={
                <Button onClick={() => selectUser('')}>查看整个房间</Button>
              }
            />
          )}
          <div className="inspector-mobile-panels">
            <Segmented
              block
              aria-label="巡检面板"
              value={mobilePanel}
              options={[
                { label: '会话', value: 'sessions' },
                { label: '消息', value: 'messages' },
                { label: '交付物', value: 'artifacts' },
              ]}
              onChange={(value) => setMobilePanel(value as typeof mobilePanel)}
            />
          </div>
          <div className={`inspector-columns is-mobile-${mobilePanel}`}>
            <aside className="inspector-rail" aria-label="成员与会话">
              <div className="inspector-members">
                <h3>
                  成员{' '}
                  <Button
                    size="small"
                    type={!store.query.userId ? 'primary' : 'default'}
                    onClick={() => selectUser('')}
                  >
                    全部
                  </Button>
                </h3>
                {overview.members.map((member, index) => (
                  <div key={member.user.userId}>
                    {member.left && !overview.members[index - 1]?.left && (
                      <h4>已退出成员</h4>
                    )}
                    <UserIdMenu userId={member.user.userId}>
                      <button
                        type="button"
                        className={`inspector-member ${store.query.userId === member.user.userId ? 'is-selected' : ''}`}
                        onClick={() => selectUser(member.user.userId)}
                      >
                        <Avatar size="small" src={member.user.avatarUrl}>
                          {(
                            member.user.displayName || member.user.userId
                          ).slice(0, 1)}
                        </Avatar>
                        <span>
                          {member.user.displayName || member.user.userId}
                          {member.role === 'owner' && <small>房主</small>}
                        </span>
                        <small>{member.sessionCount}</small>
                      </button>
                    </UserIdMenu>
                  </div>
                ))}
              </div>
              <SessionTree
                key={`${store.query.roomId}:${store.query.userId}`}
                store={store}
                select={selectSession}
              />
            </aside>
            <SessionTranscript store={store} users={users} />
            <aside className="inspector-artifacts" aria-label="交付产物">
              <h3>交付产物</h3>
              <Segmented
                size="small"
                options={[
                  { label: '本会话', value: 'session' },
                  { label: '全房间', value: 'room' },
                ]}
                value={store.artifactScope}
                onChange={(value) =>
                  store.setArtifactScope(value as 'session' | 'room')
                }
              />
              {store.artifactsLoading && <Spin />}
              {store.artifactError && (
                <Alert
                  type="error"
                  title={store.artifactError}
                  action={
                    <Button onClick={() => store.loadArtifacts()}>重试</Button>
                  }
                />
              )}
              {!store.artifactsLoading && !store.artifacts.length && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    store.artifactScope === 'session' && !store.query.sessionId
                      ? '选择会话查看产物'
                      : '暂无产物'
                  }
                />
              )}
              {store.artifacts.map((item) => (
                <button
                  type="button"
                  className="inspector-artifact"
                  key={item.artifactId}
                  onClick={() => store.openPreview(item.artifactId)}
                >
                  {item.coverUrl && (
                    <img src={item.coverUrl} alt="" loading="lazy" />
                  )}
                  <strong>
                    {item.artifactType === 'website'
                      ? '🌐 '
                      : item.artifactType === 'link'
                        ? '↗ '
                        : '📄 '}
                    {item.displayName || item.fileName}
                  </strong>
                  <small>
                    {item.owner?.displayName || item.owner?.userId} ·{' '}
                    {item.sourceAgentSessionTitle}
                  </small>
                  <small>{timestamp(item.updatedAtUnixMs)}</small>
                  <span>
                    {item.artifactType === 'website' ? '查看分享状态' : '预览'}
                  </span>
                </button>
              ))}
              {store.artifactsCursor && (
                <Button
                  block
                  loading={store.artifactsLoading}
                  onClick={() => store.loadArtifacts(true)}
                >
                  更多产物
                </Button>
              )}
            </aside>
          </div>
        </>
      )}
      <ArtifactPreview store={store} />
    </main>
  );
});
